import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertCircle,
  ArrowDown,
  ArrowRight,
  ArrowUp,
  BadgeCheck,
  ChevronLeft,
  ChevronRight,
  Filter,
  RefreshCw,
  Search,
  ShieldCheck,
  SlidersHorizontal,
  UserCheck,
  UserRound,
  Users,
  UserX,
  X,
} from "lucide-react";
import { getCurrentTokenAccessInfo } from "../../utils/jwt";
import { PaginationMeta } from "../../types/api";
import { fetchDashboard } from "../dashboard/dashboardApi";
import styles from "./UserShared.module.css";
import { fetchUsers, User } from "./userApi";

type SortOrder = "asc" | "desc";
type SortKey =
  | "name"
  | "email"
  | "user_id"
  | "status"
  | "created_at"
  | "last_login";

const PAGE_SIZE = 20;
const dateFormatter = new Intl.DateTimeFormat("tr-TR", {
  day: "2-digit",
  month: "short",
  year: "numeric",
});

function formatDate(value: unknown) {
  if (typeof value !== "string" || !value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : dateFormatter.format(date);
}

function getInitials(user: User) {
  const source = user.name || user.email || user.user_id;
  return source
    .split(/[\s@._-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");
}

function getStatus(user: User) {
  return user.status || (user.is_deleted ? "disabled" : "active");
}

function getRoles(user: User) {
  if (Array.isArray(user.roles) && user.roles.length > 0) return user.roles;
  if (typeof user.role === "string" && user.role) return [user.role];
  return user.isAdmin ? ["ADMIN"] : [];
}

export default function UserList() {
  const navigate = useNavigate();
  const access = getCurrentTokenAccessInfo();
  const canManageUsers = Boolean(access?.canManageUsers);

  const [users, setUsers] = useState<User[]>([]);
  const [meta, setMeta] = useState<PaginationMeta | null>(null);
  const [totalUsers, setTotalUsers] = useState<number | null>(null);
  const [globalActiveUsers, setGlobalActiveUsers] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [roleFilter, setRoleFilter] = useState("all");
  const [sortBy, setSortBy] = useState<SortKey>("created_at");
  const [sortOrder, setSortOrder] = useState<SortOrder>("desc");

  const loadUsers = useCallback(
    async (isRefresh = false) => {
      try {
        isRefresh ? setRefreshing(true) : setLoading(true);
        setError("");
        const response = await fetchUsers({
          page,
          limit: PAGE_SIZE,
          search: search || undefined,
          status: statusFilter === "all" ? undefined : statusFilter,
          role: roleFilter === "all" ? undefined : roleFilter,
          sort_by: sortBy,
          sort_order: sortOrder,
        });
        setUsers(response.items || []);
        setMeta(response.meta || null);
        if (
          !search &&
          statusFilter === "all" &&
          roleFilter === "all" &&
          typeof response.meta?.total === "number"
        ) {
          setTotalUsers(response.meta.total);
        }
      } catch (err: any) {
        setError(err?.message || "Kullanıcılar yüklenirken bir hata oluştu.");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [page, roleFilter, search, sortBy, sortOrder, statusFilter]
  );

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  useEffect(() => {
    fetchDashboard()
      .then((dashboard) => {
        setTotalUsers(dashboard.users.total);
        setGlobalActiveUsers(dashboard.users.active);
      })
      .catch(() => {
        // The list meta remains the fallback when dashboard summary is unavailable.
      });
  }, []);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 350);

    return () => window.clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [statusFilter, roleFilter, sortBy, sortOrder]);

  const stats = {
    total:
      totalUsers ??
      (typeof meta?.total === "number" ? meta.total : users.length),
    active:
      globalActiveUsers ??
      users.filter((user) => getStatus(user) === "active").length,
    disabled: users.filter((user) => getStatus(user) === "disabled").length,
    admins: users.filter((user) =>
      getRoles(user).some((role) => role.toUpperCase() === "ADMIN")
    ).length,
  };

  const visibleUsers = useMemo(() => {
    return users;
  }, [users]);

  const totalRows =
    typeof meta?.total === "number" ? meta.total : users.length;
  const totalPages =
    typeof meta?.total_pages === "number"
      ? Math.max(1, meta.total_pages)
      : Math.max(1, Math.ceil(totalRows / Number(meta?.limit || PAGE_SIZE)));
  const currentPage = Number(meta?.page || page);
  const canGoNext =
    typeof meta?.total === "number"
      ? currentPage < totalPages
      : users.length >= PAGE_SIZE;
  const hasFilters =
    Boolean(searchInput) ||
    statusFilter !== "all" ||
    roleFilter !== "all";

  const changeSort = (key: SortKey) => {
    if (sortBy === key) {
      setSortOrder((current) => (current === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(key);
      setSortOrder("asc");
    }
  };

  const resetFilters = () => {
    setSearchInput("");
    setSearch("");
    setStatusFilter("all");
    setRoleFilter("all");
  };

  const openUser = (user: User) => {
    if (!user.user_id) {
      setError("Bu kullanıcı kaydında detay sayfası için geçerli bir ID bulunmuyor.");
      return;
    }
    navigate(`/users/${encodeURIComponent(user.user_id)}`, { state: { user } });
  };

  const SortButton = ({
    column,
    children,
  }: {
    column: SortKey;
    children: React.ReactNode;
  }) => (
    <button
      type="button"
      className={styles.usersSortButton}
      onClick={() => changeSort(column)}
    >
      {children}
      {sortBy === column ? (
        sortOrder === "asc" ? (
          <ArrowUp size={13} />
        ) : (
          <ArrowDown size={13} />
        )
      ) : (
        <span className={styles.sortPlaceholder} />
      )}
    </button>
  );

  return (
    <div className={styles.usersPage}>
      <header className={styles.usersHero}>
        <div>
          <span className={styles.usersEyebrow}>
            <Users size={14} />
            Kullanıcı merkezi
          </span>
          <h1>Kullanıcılar</h1>
          <p>
            Hesapları, erişim durumlarını ve platform rollerini tek yerden
            görüntüle.
          </p>
        </div>
        <div className={styles.usersHeroActions}>
          <span
            className={`${styles.permissionBadge} ${
              canManageUsers ? styles.permissionAdmin : styles.permissionReadOnly
            }`}
          >
            {canManageUsers ? <ShieldCheck size={15} /> : <BadgeCheck size={15} />}
            {canManageUsers ? "Yönetim yetkisi" : "Salt okunur erişim"}
          </span>
          <button
            type="button"
            className={styles.usersRefreshButton}
            onClick={() => loadUsers(true)}
            disabled={refreshing}
          >
            <RefreshCw
              size={16}
              className={refreshing ? styles.usersSpinning : undefined}
            />
            {refreshing ? "Güncelleniyor" : "Yenile"}
          </button>
        </div>
      </header>

      <section className={styles.usersStats} aria-label="Kullanıcı özeti">
        <div>
          <span className={styles.statIconBlue}>
            <Users size={19} />
          </span>
          <p>Toplam hesap</p>
          <strong>{stats.total.toLocaleString("tr-TR")}</strong>
        </div>
        <div>
          <span className={styles.statIconGreen}>
            <UserCheck size={19} />
          </span>
          <p>Aktif hesap</p>
          <strong>{stats.active.toLocaleString("tr-TR")}</strong>
        </div>
        <div>
          <span className={styles.statIconRed}>
            <UserX size={19} />
          </span>
          <p>Devre dışı (bu sayfa)</p>
          <strong>{stats.disabled.toLocaleString("tr-TR")}</strong>
        </div>
        <div>
          <span className={styles.statIconViolet}>
            <ShieldCheck size={19} />
          </span>
          <p>Admin (bu sayfa)</p>
          <strong>{stats.admins.toLocaleString("tr-TR")}</strong>
        </div>
      </section>

      <section className={styles.usersFilterPanel}>
        <div className={styles.usersSearch}>
          <Search size={18} />
          <input
            type="search"
            aria-label="Kullanıcı ara"
            placeholder="İsim, e-posta veya kullanıcı ID ara..."
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
          />
          {searchInput && (
            <button
              type="button"
              aria-label="Aramayı temizle"
              onClick={() => {
                setSearchInput("");
                setSearch("");
              }}
            >
              <X size={15} />
            </button>
          )}
        </div>

        <div className={styles.usersFilters}>
          <label>
            <Filter size={15} />
            <select
              aria-label="Duruma göre filtrele"
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
            >
              <option value="all">Tüm durumlar</option>
              <option value="active">Aktif</option>
              <option value="disabled">Devre dışı</option>
              <option value="pending">Bekliyor</option>
            </select>
          </label>
          <label>
            <ShieldCheck size={15} />
            <select
              aria-label="Role göre filtrele"
              value={roleFilter}
              onChange={(event) => setRoleFilter(event.target.value)}
            >
              <option value="all">Tüm roller</option>
              <option value="ADMIN">Admin</option>
              <option value="CONTENT_MANAGER">Content Manager</option>
            </select>
          </label>
          {hasFilters && (
            <button
              type="button"
              className={styles.clearFiltersButton}
              onClick={resetFilters}
            >
              Filtreleri temizle
            </button>
          )}
        </div>
      </section>

      {loading ? (
        <div className={styles.usersState}>
          <div className={styles.usersLoader} />
          <h2>Kullanıcılar yükleniyor</h2>
          <p>Hesap verileri Drumly API'den alınıyor.</p>
        </div>
      ) : error ? (
        <div className={`${styles.usersState} ${styles.usersErrorState}`}>
          <AlertCircle size={32} />
          <h2>Liste yüklenemedi</h2>
          <p>{error}</p>
          <button type="button" onClick={() => loadUsers()}>
            Tekrar dene
          </button>
        </div>
      ) : visibleUsers.length === 0 ? (
        <div className={styles.usersState}>
          <UserRound size={34} />
          <h2>Kullanıcı bulunamadı</h2>
          <p>Arama veya filtreleri değiştirerek tekrar deneyin.</p>
          {hasFilters && (
            <button type="button" onClick={resetFilters}>
              Filtreleri temizle
            </button>
          )}
        </div>
      ) : (
        <>
          <section className={styles.usersTablePanel}>
            <div className={styles.usersTableHeader}>
              <div>
                <h2>Kullanıcı dizini</h2>
                <p>
                  {totalRows.toLocaleString("tr-TR")} hesap içinde bu sayfada{" "}
                  {visibleUsers.length} kayıt
                </p>
              </div>
              <span>
                <SlidersHorizontal size={14} />
                {sortBy} · {sortOrder === "asc" ? "artan" : "azalan"}
              </span>
            </div>

            <div className={styles.usersTableWrap}>
              <table className={styles.usersTable}>
                <thead>
                  <tr>
                    <th>
                      <SortButton column="name">Kullanıcı</SortButton>
                    </th>
                    <th>
                      <SortButton column="status">Durum</SortButton>
                    </th>
                    <th>Roller</th>
                    <th>Şarkı</th>
                    <th>Cihaz</th>
                    <th>
                      <SortButton column="created_at">Oluşturuldu</SortButton>
                    </th>
                    <th>
                      <SortButton column="last_login">Son giriş</SortButton>
                    </th>
                    <th aria-label="İşlem" />
                  </tr>
                </thead>
                <tbody>
                  {visibleUsers.map((user) => {
                    const status = getStatus(user);
                    const roles = getRoles(user);
                    return (
                      <tr key={user.user_id} onClick={() => openUser(user)}>
                        <td>
                          <div className={styles.usersIdentity}>
                            <span>{getInitials(user)}</span>
                            <div>
                              <strong>{user.name || "İsimsiz kullanıcı"}</strong>
                              <p>{user.email || "E-posta yok"}</p>
                              <small>{user.user_id}</small>
                            </div>
                          </div>
                        </td>
                        <td>
                          <span
                            className={`${styles.userStatus} ${
                              styles[`status${status.replace(/^\w/, (letter) =>
                                letter.toUpperCase()
                              )}`] || ""
                            }`}
                          >
                            {status}
                          </span>
                        </td>
                        <td>
                          <div className={styles.roleChips}>
                            {roles.length > 0 ? (
                              roles.map((role) => <span key={role}>{role}</span>)
                            ) : (
                              <span className={styles.emptyRole}>Rol yok</span>
                            )}
                          </div>
                        </td>
                        <td>
                          {Number(user.assigned_song_count || 0).toLocaleString(
                            "tr-TR"
                          )}
                        </td>
                        <td>
                          {Number(user.device_count || 0).toLocaleString("tr-TR")}
                        </td>
                        <td>{formatDate(user.created_at)}</td>
                        <td>{formatDate(user.last_login)}</td>
                        <td>
                          <button
                            type="button"
                            className={styles.rowAction}
                            aria-label={`${user.name || user.user_id} detayını aç`}
                            onClick={(event) => {
                              event.stopPropagation();
                              openUser(user);
                            }}
                          >
                            <ArrowRight size={17} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className={styles.userMobileGrid}>
              {visibleUsers.map((user) => {
                const status = getStatus(user);
                const roles = getRoles(user);
                return (
                  <button
                    type="button"
                    key={user.user_id}
                    className={styles.userMobileCard}
                    onClick={() => openUser(user)}
                  >
                    <div className={styles.mobileCardTop}>
                      <div className={styles.usersIdentity}>
                        <span>{getInitials(user)}</span>
                        <div>
                          <strong>{user.name || "İsimsiz kullanıcı"}</strong>
                          <p>{user.email || "E-posta yok"}</p>
                        </div>
                      </div>
                      <ChevronRight size={18} />
                    </div>
                    <div className={styles.mobileCardMeta}>
                      <span
                        className={`${styles.userStatus} ${
                          styles[`status${status.replace(/^\w/, (letter) =>
                            letter.toUpperCase()
                          )}`] || ""
                        }`}
                      >
                        {status}
                      </span>
                      <span>{Number(user.assigned_song_count || 0)} şarkı</span>
                      <span>{Number(user.device_count || 0)} cihaz</span>
                    </div>
                    <div className={styles.roleChips}>
                      {roles.length > 0 ? (
                        roles.map((role) => <span key={role}>{role}</span>)
                      ) : (
                        <span className={styles.emptyRole}>Rol yok</span>
                      )}
                    </div>
                    <small className={styles.mobileUserId}>{user.user_id}</small>
                  </button>
                );
              })}
            </div>
          </section>

          <nav className={styles.usersPagination} aria-label="Sayfalama">
            <span>
              {totalRows === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1}–
              {Math.min(currentPage * PAGE_SIZE, totalRows)} / {totalRows}
            </span>
            <div>
              <button
                type="button"
                aria-label="Önceki sayfa"
                disabled={currentPage <= 1}
                onClick={() => setPage((value) => Math.max(1, value - 1))}
              >
                <ChevronLeft size={17} />
              </button>
              <strong>
                {currentPage} / {totalPages}
              </strong>
              <button
                type="button"
                aria-label="Sonraki sayfa"
                disabled={!canGoNext}
                onClick={() =>
                  setPage((value) => Math.min(totalPages, value + 1))
                }
              >
                <ChevronRight size={17} />
              </button>
            </div>
          </nav>
        </>
      )}
    </div>
  );
}
